import { Chess, swapColor, } from "@chesshotel/chess";
import { ChessTermType, TermAction, } from "@chesshotel/types";
import { exchangeEvaluation, getFen, getSortedMovesToSquare, getThreats, uci2move, } from "./util.js";
import { isValidFenMove } from "./tactics/util.js";
function detectOfferTrade(fen, move) {
    if (!fen || !move)
        return null;
    const chess = new Chess(fen);
    let moveResult = null;
    try {
        moveResult = chess.move(uci2move(move));
    }
    catch {
        return null;
    }
    if (!moveResult)
        return null;
    if (moveResult.captured) {
        return null;
    }
    const attackers = getSortedMovesToSquare(chess, moveResult.to);
    if (attackers.length == 0) {
        return null;
    }
    const { gain } = exchangeEvaluation(chess.fen(), moveResult.to, {
        backtrackBadMoves: false,
    });
    if (gain == 0) {
        const involvedPiece = attackers[0];
        const mainPiece = {
            type: moveResult.piece,
            square: moveResult.to,
            color: moveResult.color,
        };
        const arrows = [
            {
                from: involvedPiece.square,
                to: mainPiece.square,
            },
        ];
        const translationKeys = {
            piece: mainPiece.type,
        };
        return {
            term: involvedPiece.type == moveResult.piece
                ? ChessTermType.OFFER_TRADE_SAME_PIECE
                : ChessTermType.OFFER_TRADE,
            mainPiece,
            involvedPiece,
            arrows,
            maxGain: 0,
            translationKeys,
        };
    }
}
function detectThreatenTrade(fen, move) {
    if (!fen || !move)
        return null;
    const chess = new Chess(fen);
    let moveResult = null;
    try {
        moveResult = chess.move(uci2move(move));
    }
    catch {
        return null;
    }
    if (!moveResult) {
        return null;
    }
    if (chess.inCheck()) {
        return null;
    }
    chess.changeTurn();
    const nextMoves = chess.moves({
        square: moveResult.to,
        piece: moveResult.piece,
        verbose: true,
    });
    const attacks = nextMoves
        .filter((move) => move.captured)
        .map((move) => {
        const from = chess.getBoardPiece(move.from);
        const to = chess.getBoardPiece(move.to);
        return { from, to };
    });
    if (attacks.length === 0) {
        return null;
    }
    const tradedPieces = attacks
        .map((attack) => {
        const { from, to } = attack;
        const evalResult = exchangeEvaluation(chess.fen(), to.square, {
            from: from.square,
        });
        return { ...attack, evalResult };
    })
        .filter(({ evalResult }) => evalResult.gain == 0)
        .filter(({ evalResult }) => {
        const { moveSequence } = evalResult;
        return moveSequence.find((move) => move.from == moveResult.from && move.to == moveResult.to);
    });
    if (tradedPieces.length === 0) {
        return null;
    }
    let mainPiece = {
        type: moveResult.piece,
        square: moveResult.to,
        color: moveResult.color,
    };
    const term = ChessTermType.THREATEN_TRADE;
    const trade = tradedPieces[0];
    const { from, to } = trade;
    const arrows = [{ from: from.square, to: to.square }];
    const translationKeys = {
        piece: from.type,
        piece2: to.type,
    };
    return {
        term,
        mainPiece,
        move: moveResult,
        arrows,
        translationKeys,
        maxGain: 0,
    };
}
function detectTrade(fen, move) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveFrom = move.slice(0, 2);
    const moveTo = move.slice(2, 4);
    const piece = chess.get(moveFrom);
    const mainPiece = chess.getBoardPiece(moveFrom);
    const attackedPiece = chess.getBoardPiece(moveTo);
    if (!attackedPiece) {
        return;
    }
    const { gain, moveSequence } = exchangeEvaluation(chess.fen(), moveTo, {
        filteredByLoss: true,
    });
    if (moveSequence.length == 1) {
        const threats = getThreats(chess.fen(), color);
        if (threats?.length > 0) {
            const bestThreat = threats.sort((a, b) => b.gain - a.gain)[0];
            if (bestThreat?.gain > 0) {
                const threatMove = {
                    from: bestThreat.from.square,
                    to: bestThreat.to.square,
                };
                if (bestThreat.gain - gain == 0) {
                    const arrows = [
                        { from: moveFrom, to: moveTo },
                        { from: bestThreat.from.square, to: bestThreat.to.square },
                    ];
                    const translationKeys = {
                        piece: piece.type,
                        piece2: bestThreat.to.type,
                    };
                    return {
                        arrows,
                        term: ChessTermType.TRADE,
                        termAction: TermAction.PERFORM,
                        mainPiece,
                        attackedPiece,
                        maxGain: 0,
                        translationKeys,
                    };
                }
            }
        }
    }
    else if (gain == 0 && moveSequence.length > 1) {
        const move2 = moveSequence[1];
        const arrows = [
            { from: moveFrom, to: moveTo },
            { from: move2.from, to: move2.to },
        ];
        const piece1 = chess.get(move2.to);
        const translationKeys = {
            piece2: piece1.type,
            piece: piece.type,
            color,
        };
        return {
            arrows,
            term: ChessTermType.TRADE,
            termAction: TermAction.PERFORM,
            mainPiece,
            attackedPiece,
            maxGain: 0,
            translationKeys,
        };
    }
}
export function detectNegativeOfferTrade(position, lastPosition) {
    const userMove = detectOfferTrade(lastPosition.fen, position.move);
    const bestMove = detectOfferTrade(lastPosition.fen, lastPosition.bestMove);
    if (bestMove && !userMove) {
        return { ...bestMove, termAction: TermAction.MISS };
    }
    if (bestMove && userMove) {
        return { ...bestMove, termAction: TermAction.BETTER_WAY };
    }
}
export function detectPositiveTrades(position, lastPosition) {
    const trade = detectTrade(lastPosition.fen, position.move);
    if (trade) {
        if (position.chessTerm?.term === ChessTermType.RETAKE ||
            position.chessTerm?.term === ChessTermType.TAKES_BACK ||
            position.chessTerm?.term === ChessTermType.TRADE) {
            return null;
        }
        return {
            ...trade,
            termAction: TermAction.PERFORM,
        };
    }
    const offerTrade = detectOfferTrade(lastPosition.fen, position.move);
    if (offerTrade) {
        return {
            ...offerTrade,
            termAction: TermAction.PERFORM,
        };
    }
    const threatenTrade = detectThreatenTrade(lastPosition.fen, position.move);
    if (threatenTrade) {
        return {
            ...threatenTrade,
            termAction: TermAction.PERFORM,
        };
    }
}
export function detectNegativeTrade(position, lastPosition) {
    const userMove = detectTrade(lastPosition.fen, position.move);
    const bestMove = detectTrade(lastPosition.fen, lastPosition.bestMove);
    const arrows = lastPosition.pvMoves.length > 1
        ? lastPosition.pvMoves.slice(0, 2).map((move) => uci2move(move))
        : null;
    if (bestMove && !userMove) {
        return {
            ...bestMove,
            termAction: TermAction.MISS,
            arrows: arrows || bestMove.arrows,
        };
    }
    if (bestMove && userMove) {
        return {
            ...bestMove,
            termAction: TermAction.BETTER_WAY,
            arrows: arrows || bestMove.arrows,
        };
    }
    if (!bestMove && userMove) {
        return {
            ...userMove,
            term: ChessTermType.BAD_EQUAL_TRADE,
            termAction: TermAction.PERFORM,
        };
    }
    const opponentMove = position.pvMoves[0];
    const userAllow = detectTrade(position.fen, opponentMove);
    if (userAllow) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
        const bestAllow = detectTrade(bestFen, opponentMove);
        if (!bestAllow) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
    }
}
//# sourceMappingURL=offerTrade.js.map